import { ApiResponse } from '@/types';
import { AxiosError } from 'axios';
import { camelize } from 'humps';
import { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { showAlertError } from './alert.utils';
import { ERROR_UNEXPECTED } from '@/constants';

// Extracts and cleans pasted text by stripping whitespace.
// Pass stripDomain: true to also remove everything from '@' onward (useful for email prefix inputs).
export const getCleanedPastedText = (
  e: React.ClipboardEvent<HTMLInputElement>,
  options?: { stripDomain?: boolean },
): string => {
  e.preventDefault();
  const pasted = e.clipboardData.getData('text');
  let cleaned = pasted.replace(/\s/g, '');
  if (options?.stripDomain) {
    const atIndex = cleaned.indexOf('@');
    if (atIndex !== -1) {
      cleaned = cleaned.substring(0, atIndex);
    }
  }
  return cleaned;
};

// Create strongly-typed form field names that return string literal types
export const createNameofProxy = <T extends Record<string, any>>() => {
  // -? - Minus optional modifier that removes optionality so the type is always defined
  // : K - Return the key as string literal type
  return new Proxy({} as { [K in keyof T]-?: K }, {
    get: (_target, prop) => prop,
  });
};

// Extracts only the dirty (modified) fields from form data
export const getDirtyFields = <T>(
  data: T,
  dirtyFields: Partial<Record<keyof T, boolean[] | boolean | undefined>>,
): Partial<T> => {
  const dirtyData: Partial<T> = {};
  Object.keys(dirtyFields).forEach((key) => {
    const fieldKey = key as keyof T;
    if (dirtyFields[fieldKey]) {
      dirtyData[fieldKey] = data[fieldKey];
    }
  });

  return dirtyData;
};

// Maps server validation errors to react-hook-form errors
export const handleServerValidationErrors = <T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
): void => {
  if (error instanceof AxiosError) {
    const apiResponse = error.response?.data as ApiResponse<unknown> | undefined;
    let isErrorHandled = false;

    // Check if the response contains validation errors
    if (apiResponse?.errors && typeof apiResponse.errors === 'object') {
      const errors = apiResponse.errors;

      // Iterate through each field error
      Object.entries(errors).forEach(([fieldName, messages]) => {
        if (messages && messages.length > 0) {
          const camelCaseFieldName = camelize(fieldName);
          setError(camelCaseFieldName as Path<T>, {
            type: 'server',
            message: messages.join(', '), // Join multiple messages with comma
          });
          isErrorHandled = true;
        }
      });
    }

    if (!isErrorHandled) {
      // If no validation errors were found, alert a generic error message
      showAlertError(apiResponse?.errorMessage || ERROR_UNEXPECTED);
    }
  }
};
